/**
 * CoPilot domain registry — the one list of areas the Companion can be in.
 *
 * A domain is where the user is when they talk to the CoPilot (a hero, a
 * platform area, or `general`). It replaces the closed hero unions that each
 * layer used to keep for itself (the web route regex, the mobile segment list,
 * platform-api's request enum, the BRAIN's `contextType`), which had drifted
 * apart: QultureUp and Passport reached the BRAIN as `general`, and a new hero
 * would have too. Every layer now validates the key against this list, end to
 * end, and every domain has its CoPilot domain profile in the BRAIN.
 *
 * `scope` says how a domain's profile is kept: one for every country
 * (`global`), or one per country the domain serves (`country` — read for the
 * country of the user's journey, never another country's).
 *
 * `routeSegments` are the path segments that put a page in the domain, on the
 * web and on the phone alike.
 */

export const COPILOT_DOMAIN_REGISTRY = {
  general: { scope: 'global', routeSegments: [] },
  checkup: { scope: 'global', routeSegments: ['checkup'] },
  teleportal: { scope: 'global', routeSegments: ['teleportal'] },
  qultureup: { scope: 'global', routeSegments: ['qultureup'] },
  care: { scope: 'global', routeSegments: ['care'] },
  passport: { scope: 'global', routeSegments: ['passport'] },
  career: { scope: 'global', routeSegments: ['career'] },
  testit: { scope: 'global', routeSegments: ['testit'] },
  inout: { scope: 'country', routeSegments: ['inout'] },
  vault: { scope: 'global', routeSegments: ['vault'] },
  billing: { scope: 'global', routeSegments: ['billing'] },
  profile: { scope: 'global', routeSegments: ['profile'] },
} as const satisfies Record<
  string,
  { scope: 'global' | 'country'; routeSegments: readonly string[] }
>;

export type CoPilotDomain = keyof typeof COPILOT_DOMAIN_REGISTRY;

export type CoPilotDomainScope = (typeof COPILOT_DOMAIN_REGISTRY)[CoPilotDomain]['scope'];

/** Every domain, in registry order — for enums (`z.enum`, `@IsIn`). */
export const COPILOT_DOMAINS = Object.keys(COPILOT_DOMAIN_REGISTRY) as [
  CoPilotDomain,
  ...CoPilotDomain[],
];

/** The domain a turn falls back to when the page names none. */
export const DEFAULT_COPILOT_DOMAIN: CoPilotDomain = 'general';

export function isCoPilotDomain(value: unknown): value is CoPilotDomain {
  return (
    typeof value === 'string' &&
    Object.prototype.hasOwnProperty.call(COPILOT_DOMAIN_REGISTRY, value)
  );
}

export function copilotDomainScope(domain: CoPilotDomain): CoPilotDomainScope {
  return COPILOT_DOMAIN_REGISTRY[domain].scope;
}

const DOMAIN_BY_SEGMENT: ReadonlyMap<string, CoPilotDomain> = new Map(
  (Object.keys(COPILOT_DOMAIN_REGISTRY) as CoPilotDomain[]).flatMap((domain) =>
    COPILOT_DOMAIN_REGISTRY[domain].routeSegments.map((segment) => [segment, domain] as const)
  )
);

/**
 * The domain of a page, from its path (`/checkup/results`) or its route
 * segments (`['(tabs)', 'care']`): the first segment that names one.
 * `undefined` for a page in no domain — the turn then runs as `general`.
 */
export function copilotDomainForPath(path: string | readonly string[]): CoPilotDomain | undefined {
  const segments = typeof path === 'string' ? path.split(/[/?#]/) : path;
  for (const segment of segments) {
    const domain = DOMAIN_BY_SEGMENT.get(segment);
    if (domain) return domain;
  }
  return undefined;
}
